import DashboardContainer from "components/dashboard/DashboardContainer";
import TaskTabBar from "components/tasks/taskTabBar";
import { SalesTaxTrackerHeader } from "./SalesTaxTrackerHeader";
import { SalesTaxTrackerTable } from "./SalesTaxTrackerTable";
import { useFilters } from "hooks/useFilter";
import { useModal } from "hooks/useModal";
import { SalesTaxTrackerSlider } from "./SalesTaxTrackerSlider";
import {
  TASK_TYPE,
  TaskTrackerType,
  Transaction,
  useGetSalesTaxTaskQuery,
} from "store/apis/taskTrackerSalesTax";
import { useQuery } from "hooks/useQuery";
import { ReactNode, useState } from "react";
import { Row } from "react-table-8.10.7";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { FilterOption } from "./TaskTrackerOptions";

export type TaskTrackerTrackerFilterType = {
  updateFilter: <S extends keyof FilterOption>(
    name: S,
    newValue: FilterOption[S]
  ) => void;
  values: FilterOption;
};

const initialValue: FilterOption = {
  STATE: "",
  TASK_STATUS: "",
  PAYMENT_SCHEDULE: "",
  SERVICE: "",
  SERVICE_TEAM_IDS: "",
  UNASSIGNED_SERVICE_TEAM: false,
  UNASSIGNED_PREPARER: false,
  DUE_FILINGS: true,
  PENDING_REGISTRATIONS: true,
};

export const ConditionalRender = ({
  children,
  fallback,
  condition,
}: {
  children: ReactNode;
  fallback: ReactNode;
  condition: boolean;
}) => {
  return condition ? <>{children}</> : <>{fallback}</>;
};

export const SalesTaxTracker = () => {
  const { values, updateFilter } = useFilters<FilterOption>({
    initialValue,
    useQueryParams: true,
  });
  const [selectedTask, setSelectedTask] = useState({
    uuid: "",
    entity: "",
    entityName: "",
    groupId: "",
    groupName: "",
    channelId: "",
  });
  const salesTaxTrackerSlider = useModal();
  const query = useQuery();

  const page = Number(query.get("page") || "1");
  const searchTerm = query.get("search_term") || "";
  const selectedTab = query.get("tab") || TASK_TYPE.FILINGS;

  const { data, isLoading, isFetching, isSuccess } = useGetSalesTaxTaskQuery(
    {
      type: selectedTab as TaskTrackerType,
      state: values.STATE,
      status: values.TASK_STATUS,
      paymentSchedule: values.PAYMENT_SCHEDULE,
      page,
      searchTerm: searchTerm,
      dueFilings: values.DUE_FILINGS,
      unassignedServiceTeam: values.UNASSIGNED_SERVICE_TEAM,
      unassignedPreparer: values.UNASSIGNED_PREPARER,
      pending_registrations: values.PENDING_REGISTRATIONS,
      serviceTeamIds: values.SERVICE_TEAM_IDS,
    },
    { refetchOnMountOrArgChange: true }
  );

  const [defaultSliderTab, setDefaultSliderTab] = useState<"DETAILS" | "CHAT">(
    "DETAILS"
  );

  const handleRowClick = (data: Row<Transaction>, openChat = false) => {
    setSelectedTask({
      uuid: data?.original?.uuid,
      entity: data?.original?.entity?.uuid,
      entityName: data?.original?.entity?.name,
      groupName: data?.original?.group?.name,
      groupId: data?.original?.group?.uuid,
      channelId: data?.original?.channel_url || "",
    });

    if (openChat) {
      setDefaultSliderTab("CHAT");
    } else {
      setDefaultSliderTab("DETAILS");
    }
    salesTaxTrackerSlider.open();
  };

  const resetFilter = () => {
    Object.keys(initialValue).forEach((key) => {
      updateFilter(
        key as keyof FilterOption,
        initialValue[key as keyof FilterOption]
      );
    });
  };

  return (
    <DashboardContainer className="t-h-full">
      <DashboardContainer.Header>
        <TaskTabBar defaultValue="/admin/salestax-tracker" />
      </DashboardContainer.Header>
      <div className="t-px-5 t-pb-4 t-flex t-flex-col t-gap-4 t-h-full t-overflow-hidden">
        <SalesTaxTrackerHeader
          totalPage={data?.total_pages}
          filter={{ updateFilter, values }}
          itemsPerPage={data?.per_page}
          totalItemCount={data?.total_count}
          resetFilter={resetFilter}
          isFetching={isFetching}
        />
        <Async.Root
          isLoading={isLoading}
          isEmpty={false}
          isSuccess={Boolean(isSuccess)}
        >
          <Async.Success>
            <div className="t-flex-1 t-min-h-0">
              <SalesTaxTrackerTable
                data={data?.transactions || []}
                onRowClick={handleRowClick}
                perPage={data?.per_page || 20}
                filter={{ updateFilter, values }}
                isEmpty={!data?.transactions?.length}
              />
            </div>
          </Async.Success>
        </Async.Root>
        <SalesTaxTrackerSlider
          isOpen={salesTaxTrackerSlider.isOpen}
          onClose={salesTaxTrackerSlider.close}
          taskData={selectedTask}
          defaultSliderTab={defaultSliderTab}
        />
      </div>
    </DashboardContainer>
  );
};
